import { useCallback, useEffect, useMemo, useState } from 'react';
import TempEdgeApi from '../../../services/TempEdgeApi';
import { BACKEND_TYPES } from '../../../services/ApiConfig';
import { REACT_APP_URL_V2_NOTIFICATION_FIND_ALL } from '../Routes/URLs';
import NotificationIcon from './NotificationIcon';
import NewsStack from './NewsStack';
import './Notifications.css';
import { useLocation } from 'react-router-dom';

const extractNotifications = (data) => {
  const result = data?.result ?? data?.results ?? data?.data ?? data?.notifications ?? data?.notificationList;
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.notifications)) return result.notifications;
  if (Array.isArray(result?.list)) return result.list;
  return [];
};

const Notifications = () => {
  const [show, setShow] = useState(false);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(false);
  const [hasBeenRead, setHasBeenRead] = useState(false);
  const location = useLocation();

  const getUserName = useMemo(() => {
    try {
      const agency = JSON.parse(localStorage.getItem('agency') || '{}');
      return agency?.user?.username || agency?.user?.userName || agency?.username || agency?.userName || '';
    } catch {
      return '';
    }
  }, []);

  const [userName, setUserName] = useState(getUserName);

  const getOrgId = useMemo(() => {
    try {
      const agency = JSON.parse(localStorage.getItem('agency') || '{}');
      return agency?.organizationEntity?.orgId ?? '';
    } catch {
      return '';
    }
  }, []);

  const [orgId, setOrgId] = useState(getOrgId);

  useEffect(() => {
    const handleStorageUpdate = () => {
      setUserName(getUserName);
      setOrgId(getOrgId);
    };

    window.addEventListener('storage', handleStorageUpdate);
    window.addEventListener('localStorageUpdate', handleStorageUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageUpdate);
      window.removeEventListener('localStorageUpdate', handleStorageUpdate);
    };
  }, [getUserName]);

  // Reset UI state when user changes (logout/login or organization selection)
  useEffect(() => {
    setShow(false);
    setItems([]);
    setActive(false);
    setHasBeenRead(false);
  }, [userName, orgId]);

  const isDashboard = location.pathname?.includes('/dashboard');

  useEffect(() => {
    if (!isDashboard) setShow(false);
  }, [isDashboard]);

  const fetchNotifications = useCallback(
    async (includeSize = false) => {
      try {
        const payload = {
          ...(orgId ? { orgId: Number(orgId) } : {}),
          ...(userName ? { userName } : {}),
          ...(includeSize ? { size: 5 } : {})
        };

        const { data } = await TempEdgeApi.post({
          url: REACT_APP_URL_V2_NOTIFICATION_FIND_ALL,
          payload,
          backendType: BACKEND_TYPES.NEW
        });
        const list = extractNotifications(data);
        setItems(list);
        setActive(list.length > 0);
        return list;
      } catch {
        setItems([]);
        setActive(false);
        return [];
      }
    },
    [orgId, userName]
  );

  const open = useCallback(async () => {
    const list = await fetchNotifications(true);
    if (!list || list.length === 0) {
      setShow(false);
      setActive(false);
      setHasBeenRead(true);
      return;
    }

    setShow(true);
  }, [fetchNotifications]);

  useEffect(() => {
    const isDashboard = location.pathname?.includes('/dashboard');

    if (hasBeenRead) return;
    if (!isDashboard) return;

    let cancelled = false;
    const run = async () => {
      const list = await fetchNotifications();
      if (!cancelled) {
        if (list.length > 0) {
          setShow(true);
        } else {
          // Gris: no hay notificaciones para este usuario
          setHasBeenRead(true);
          setShow(false);
          setActive(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [fetchNotifications, hasBeenRead, location.pathname]);

  const handleClose = useCallback(() => {
    setShow(false);
    setHasBeenRead(true);
    setActive(false);
  }, []);

  return (
    <>
      <NotificationIcon onClick={open} active={active} read={hasBeenRead} />
      <NewsStack show={show} items={items} onClose={handleClose} />
    </>
  );
};

export default Notifications;
